<template>
  <div class="container py-4 dashboard-canvas">
    <div
      class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-4"
    >
      <div>
        <h4 class="mb-1 akkurate-dark">{{ greeting }}, {{ userFirstName }}</h4>
        <p class="text-muted mb-0 small">
          {{ personaTitle }} · {{ storeName }}
        </p>
      </div>

      <icon-button
        :func="() => load()"
        :icon="'rotate-right'"
        :title="'Refresh'"
        :classes="'akkurate-green-btn btn-size2'"
        :btnName="'Refresh'"
      />
    </div>

    <div v-if="loading && !dashboard" class="text-center py-5 text-muted">
      Loading dashboard…
    </div>

    <template v-else-if="dashboard">
      <!-- Sales / cashier -->
      <section
        v-if="dashboard.sales"
        class="dashboard-section page-card page-card--float mb-3"
      >
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0 dashboard-section-title">Today's sales</h5>

          <link-button
            v-if="canSell"
            :to="'/pos'"
            :classes="'akkurate-link-btn btn-size2'"
            :btnName="'Open POS'"
            :icon="'arrow-right'"
            :title="'Open POS'"
          />
        </div>
        <report-summary-cards :cards="salesCards" />
        <p
          v-if="dashboard.sales.today.vsYesterdayPercent !== undefined"
          class="small text-muted mb-3"
        >
          <span :class="changeClass(dashboard.sales.today.vsYesterdayPercent)">
            {{ formatChange(dashboard.sales.today.vsYesterdayPercent) }}
          </span>
          vs yesterday
        </p>
        <div v-if="canAuditFloor && dashboard.sales.myToday" class="mb-3">
          <span class="badge bg-light text-dark border">
            Your sales today: {{ dashboard.sales.myToday.transactionCount }} ·
            {{ formatMoney(dashboard.sales.myToday.totalAmount) }}
          </span>
        </div>

        <div
          v-if="dashboard.sales.heldCount"
          class="sales-held-banner d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3"
        >
          <span>
            <strong>{{ dashboard.sales.heldCount }}</strong>
            held {{ dashboard.sales.heldCount === 1 ? "sale" : "sales" }}
            {{ canAuditFloor ? "waiting on this floor" : "waiting (yours)" }}
          </span>
          <link-button
            to="/sales?status=held"
            classes="akkurate-link-btn"
            btn-name="Manage held"
            icon="arrow-right"
            title="View held sales"
          />
        </div>

        <div class="row g-3">
          <div v-if="canAuditFloor" class="col-lg-7">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="small text-muted mb-0">Cash &amp; sales by cashier</h6>
              <link-button
                to="/sales?paymentMethod=Cash"
                classes="akkurate-link-btn"
                btn-name="Cash sales"
                icon=""
                title="View cash sales"
              />
            </div>
            <b-table
              :items="dashboard.sales.byCashier || []"
              :fields="cashierFields"
              small
              responsive
            >
              <template #cell(cashierName)="data">
                {{ data.value }}
                <span
                  v-if="isMyCashier(data.item)"
                  class="badge bg-light text-dark border ms-1"
                  >You</span
                >
              </template>
              <template #cell(totalAmount)="data">{{
                formatMoney(data.value)
              }}</template>
              <template #cell(cashAmount)="data">{{
                formatMoney(data.value)
              }}</template>
            </b-table>
            <p
              v-if="!(dashboard.sales.byCashier || []).length"
              class="small text-muted"
            >
              No completed sales today yet.
            </p>
          </div>
          <div :class="canAuditFloor ? 'col-lg-5' : 'col-12'">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="small text-muted mb-0">Held sales</h6>
              <link-button
                v-if="!canAuditFloor"
                to="/sales?paymentMethod=Cash"
                classes="akkurate-link-btn"
                btn-name="My cash sales"
                icon=""
                title="View your cash sales"
              />
            </div>
            <b-table
              v-if="dashboard.sales.heldSales?.length"
              :items="dashboard.sales.heldSales"
              :fields="heldTableFields"
              small
              responsive
            >
              <template #cell(total)="data">{{
                formatMoney(data.value)
              }}</template>
              <template #cell(heldAt)="data">{{
                formatTime(data.value)
              }}</template>
            </b-table>
            <p v-else class="small text-muted">No held sales right now.</p>
          </div>
        </div>

        <div class="row g-3 mt-1">
          <div class="col-md-5">
            <h6 class="small text-muted">Payments</h6>
            <b-table
              :items="dashboard.sales.paymentsByMethod || []"
              :fields="paymentFields"
              small
              responsive
            >
              <template #cell(totalAmount)="data">{{
                formatMoney(data.value)
              }}</template>
            </b-table>
          </div>
          <div class="col-md-7">
            <h6 class="small text-muted">Recent sales</h6>
            <b-table
              :items="dashboard.sales.recentSales || []"
              :fields="recentSaleTableFields"
              small
              responsive
            >
              <template #cell(paymentMethods)="data">
                {{
                  Array.isArray(data.value) && data.value.length
                    ? data.value.join(", ")
                    : "—"
                }}
              </template>
              <template #cell(total)="data">{{
                formatMoney(data.value)
              }}</template>
              <template #cell(completedAt)="data">{{
                formatTime(data.value)
              }}</template>
            </b-table>
          </div>
        </div>
      </section>

      <!-- Cash drawer -->
      <section
        v-if="dashboard.cashDrawer"
        class="dashboard-section page-card page-card--float mb-3"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0 dashboard-section-title">Cash drawer</h5>
          <link-button
            :to="'/cash-drawer'"
            :classes="'akkurate-link-btn btn-size2'"
            :btnName="'Cash drawer'"
            :icon="'arrow-right'"
            :title="'Cash drawer'"
          />
        </div>
        <b-alert
          :variant="dashboard.cashDrawer.isOpen ? 'success' : 'secondary'"
          show
          class="mb-0"
        >
          <template
            v-if="dashboard.cashDrawer.isOpen && dashboard.cashDrawer.session"
          >
            Drawer open · float
            {{ formatMoney(dashboard.cashDrawer.session.openingFloat) }} ·
            {{ dashboard.cashDrawer.session.userName }}
          </template>
          <template v-else>No open drawer session for this store.</template>
        </b-alert>
      </section>

      <div class="row g-3 mb-3">
        <!-- Inventory -->
        <div v-if="dashboard.inventory" class="col-lg-6">
          <section class="dashboard-section page-card page-card--float h-100">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="mb-0 dashboard-section-title">Inventory</h5>
              <link-button
                :to="'/inventory'"
                :classes="'akkurate-link-btn btn-size2'"
                :btnName="'View all'"
                :icon="'arrow-right'"
                :title="'View all inventory'"
              />
            </div>
            <report-summary-cards :cards="inventoryCards" />
            <h6
              v-if="dashboard.inventory.lowStockItems?.length"
              class="small text-muted mt-3"
            >
              Low stock
            </h6>
            <b-table
              v-if="dashboard.inventory.lowStockItems?.length"
              :items="dashboard.inventory.lowStockItems"
              :fields="lowStockFields"
              small
              responsive
            />
          </section>
        </div>

        <!-- Reports snapshot -->
        <div v-if="dashboard.reports" class="col-lg-6">
          <section class="dashboard-section page-card page-card--float h-100">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="mb-0 dashboard-section-title">Sales trend (7 days)</h5>
              <link-button
                :to="'/reports'"
                :classes="'akkurate-link-btn btn-size2'"
                :btnName="'Reports'"
                :icon="'arrow-right'"
                :title="'View reports'"
              />
            </div>
            <report-summary-cards :cards="weekCards" />
            <div
              v-if="dashboard.reports.trend?.length"
              class="trend-chart mt-3"
            >
              <div
                v-for="point in dashboard.reports.trend"
                :key="point.date"
                class="trend-bar-row"
              >
                <span class="trend-label">{{ point.label }}</span>
                <div class="trend-bar-track">
                  <div
                    class="trend-bar-fill"
                    :style="{ width: barWidth(point.totalAmount) }"
                  />
                </div>
                <span class="trend-value">{{
                  formatMoney(point.totalAmount)
                }}</span>
              </div>
            </div>
            <h6
              v-if="dashboard.reports.topProducts?.length"
              class="small text-muted mt-3"
            >
              Top products this month
            </h6>
            <b-table
              v-if="dashboard.reports.topProducts?.length"
              :items="dashboard.reports.topProducts"
              :fields="topProductFields"
              small
              responsive
            >
              <template #cell(totalRevenue)="data">{{
                formatMoney(data.value)
              }}</template>
            </b-table>
          </section>
        </div>
      </div>

      <div class="row g-3 mb-3">
        <!-- Accounting -->
        <div v-if="dashboard.accounting" class="col-lg-6">
          <section class="dashboard-section page-card page-card--float h-100">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="mb-0 dashboard-section-title">
                Accounting (this month)
              </h5>
              <link-button
                :to="'/accounting'"
                :classes="'akkurate-link-btn btn-size2'"
                :btnName="'Open'"
                :icon="'arrow-right'"
                :title="'View Accounting'"
              />
            </div>
            <report-summary-cards :cards="accountingCards" />
          </section>
        </div>

        <!-- Tax / admin -->
        <div v-if="dashboard.tax || dashboard.admin" class="col-lg-6">
          <section class="dashboard-section page-card page-card--float h-100">
            <h5 class="mb-3 dashboard-section-title">Compliance & admin</h5>
            <div v-if="dashboard.tax" class="mb-3">
              <div class="small text-muted">GRA tax compliance</div>
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <b-badge
                  :variant="
                    dashboard.tax.complianceStatus === 'Settled'
                      ? 'success'
                      : 'warning'
                  "
                >
                  {{ dashboard.tax.complianceStatus }}
                </b-badge>
                <span v-if="dashboard.tax.balanceDue > 0">
                  Balance {{ formatMoney(dashboard.tax.balanceDue) }} · due
                  {{ dashboard.tax.dueDate }}
                </span>
                <span v-else class="text-muted small"
                  >No outstanding balance</span
                >
              </div>
            </div>
            <report-summary-cards v-if="dashboard.admin" :cards="adminCards" />
          </section>
        </div>
      </div>

      <!-- Quick links -->
      <section
        v-if="dashboard.quickLinks?.length"
        class="dashboard-section page-card page-card--float"
      >
        <h5 class="mb-3 dashboard-section-title">Quick actions</h5>
        <div class="quick-links">
          <!-- <router-link
            
            
            :to=""
            class="btn btn-outline-secondary btn-sm"
          >
            {{  }}
          </router-link> -->
          <quick-link
            v-for="link in dashboard.quickLinks"
            :to="link.route"
            :key="link.route"
            :classes="'akkurate-link-btn'"
            :btnName="link.label"
            :icon="link.icon"
            :title="link.label"
          />
        </div>
      </section>
    </template>

    <div v-else-if="!loading" class="page-card page-card--float text-muted">
      Select a store to view your dashboard.
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import ReportSummaryCards from "@/views/main/pages/reports/ReportSummaryCards.vue";
import IconButton from "@/components/IconButton.vue";
import LinkButton from "@/components/LinkButton.vue";
import QuickLink from "@/components/QuickLink.vue";

const PERSONA_TITLES = {
  administrator: "Administrator dashboard",
  manager: "Manager dashboard",
  cashier: "Cashier dashboard",
  inventory: "Inventory dashboard",
  accountant: "Accounting dashboard",
  analyst: "Reports dashboard",
  operations: "Operations dashboard",
};

export default {
  name: "Home",
  components: { ReportSummaryCards, IconButton, LinkButton, QuickLink },
  data() {
    return {
      loading: false,
      dashboard: null,
      trendMax: 0,
      paymentFields: [
        { key: "method", label: "Method" },
        { key: "count", label: "Count" },
        { key: "totalAmount", label: "Amount" },
      ],
      recentSaleFields: [
        { key: "saleNumber", label: "Sale #" },
        { key: "cashierName", label: "Cashier" },
        { key: "paymentMethods", label: "Payment" },
        { key: "total", label: "Total" },
        { key: "completedAt", label: "Time" },
      ],
      cashierFields: [
        { key: "cashierName", label: "Cashier" },
        { key: "salesCount", label: "Sales" },
        { key: "cashAmount", label: "Cash" },
        { key: "totalAmount", label: "Total" },
        { key: "heldCount", label: "Held" },
      ],
      heldFields: [
        { key: "holdLabel", label: "Tip" },
        { key: "cashierName", label: "Cashier" },
        { key: "total", label: "Total" },
        { key: "heldAt", label: "Held" },
      ],
      lowStockFields: [
        { key: "productSku", label: "SKU" },
        { key: "productName", label: "Product" },
        { key: "available", label: "Available" },
      ],
      topProductFields: [
        { key: "productSku", label: "SKU" },
        { key: "productName", label: "Product" },
        { key: "quantitySold", label: "Qty" },
        { key: "totalRevenue", label: "Revenue" },
      ],
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "selectedStoreId",
      "selectedStore",
      "hasPermission",
      "currencySymbol",
    ]),
    userFirstName() {
      return this.currentUser?.firstName || "there";
    },
    storeName() {
      return this.selectedStore?.storeName || "—";
    },
    personaTitle() {
      const key = this.dashboard?.persona || "operations";
      return PERSONA_TITLES[key] || "Dashboard";
    },
    greeting() {
      const hour = new Date().getHours();
      if (hour < 12) return "Good morning";
      if (hour < 17) return "Good afternoon";
      return "Good evening";
    },
    canSell() {
      return this.hasPermission("SALE");
    },
    canAuditFloor() {
      return this.hasPermission("SALES_AUDIT");
    },
    heldTableFields() {
      if (this.canAuditFloor) return this.heldFields;
      return this.heldFields.filter((f) => f.key !== "cashierName");
    },
    recentSaleTableFields() {
      if (this.canAuditFloor) return this.recentSaleFields;
      return this.recentSaleFields.filter((f) => f.key !== "cashierName");
    },
    salesCards() {
      if (!this.dashboard?.sales) return [];
      const t = this.dashboard.sales.today;
      return [
        { label: "Transactions", value: t.transactionCount },
        { label: "Total sales", value: t.totalAmount, format: "money" },
        { label: "Cash today", value: t.cashAmount ?? 0, format: "money" },
        { label: "Held", value: this.dashboard.sales.heldCount || 0 },
      ];
    },
    inventoryCards() {
      if (!this.dashboard?.inventory) return [];
      const i = this.dashboard.inventory;
      return [
        { label: "Low stock", value: i.lowStockCount },
        { label: "Out of stock", value: i.outOfStockCount },
        { label: "Pending transfers", value: i.pendingTransfers },
        { label: "Stock value", value: i.totalStockValue, format: "money" },
      ];
    },
    weekCards() {
      if (!this.dashboard?.reports) return [];
      const w = this.dashboard.reports.week;
      return [
        { label: "Week transactions", value: w.transactionCount },
        { label: "Week sales", value: w.totalAmount, format: "money" },
      ];
    },
    accountingCards() {
      if (!this.dashboard?.accounting) return [];
      const m = this.dashboard.accounting.month;
      return [
        { label: "Income", value: m.income, format: "money" },
        { label: "Expenses", value: m.expenses, format: "money" },
        { label: "Net profit", value: m.netProfit, format: "money" },
      ];
    },
    adminCards() {
      if (!this.dashboard?.admin) return [];
      const a = this.dashboard.admin;
      return [
        { label: "Store users", value: a.storeUserCount },
        { label: "Open drawers", value: a.openCashDrawers },
      ];
    },
  },
  watch: {
    selectedStoreId() {
      this.load();
    },
  },
  async mounted() {
    await this.$store.dispatch("loadSettings");
    this.load();
  },
  methods: {
    formatTime(iso) {
      if (!iso) return "—";
      return new Date(iso).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    isMyCashier(row) {
      return !!(this.currentUser?.id && row.userId === this.currentUser.id);
    },
    formatChange(pct) {
      const sign = pct > 0 ? "+" : "";
      return `${sign}${pct}%`;
    },
    changeClass(pct) {
      if (pct > 0) return "text-success";
      if (pct < 0) return "text-danger";
      return "text-muted";
    },
    barWidth(amount) {
      if (!this.trendMax) return "0%";
      return `${Math.max(2, (amount / this.trendMax) * 100)}%`;
    },
    async load() {
      if (!this.selectedStoreId) {
        this.dashboard = null;
        return;
      }
      this.loading = true;
      try {
        this.dashboard = await ApiService.get("dashboard", {
          storeId: this.selectedStoreId,
        });
        const points = this.dashboard.reports?.trend || [];
        this.trendMax = Math.max(0, ...points.map((p) => p.totalAmount));
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Failed to load dashboard",
          icon: "danger",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.dashboard-section h5.dashboard-section-title {
  font-size: 1rem;
}
.sales-held-banner {
  padding: 0.75rem 1rem;
  background: #fff8e6;
  border: 1px solid #f5d889;
  border-radius: 8px;
  font-size: 0.9rem;
}
</style>
